// Algorithm + filter catalog, driven by the Lib schema snapshot bundled at
// build time (src/config/schema/*.json — see schema/README.md). The C++
// ReconciliationWrapper owns the contract (SPEC/1489) and the backend never
// serves it, so the parameter schemas live here as a generated copy. UI
// presentation (labels, which ints are column selectors) is layered on top in
// catalogOverlay.ts — this file is the contract, that one is the chrome.

import algorithmsSchemaJson from './schema/algorithms.schema.json';
import filtersSchemaJson from './schema/filters.schema.json';

export type ParamType = 'int' | 'double' | 'bool' | 'string' | 'int[]' | 'double[]' | 'string[]';

export interface ParamSpec {
  name: string;
  type: ParamType;
  required: boolean;
  /** Present only when the Lib schema declares a default. */
  default?: unknown;
}

export interface AlgorithmSchema {
  name: string;
  params: ParamSpec[];
}

export interface FilterSchema {
  type: string;
  /** Deferred filters (e.g. `combined`) reference other filter ids. */
  deferred: boolean;
  params: ParamSpec[];
}

export const ALGORITHM_SCHEMAS = algorithmsSchemaJson as unknown as AlgorithmSchema[];
export const FILTER_SCHEMAS = filtersSchemaJson as unknown as FilterSchema[];

export function listAlgorithms(): string[] {
  return ALGORITHM_SCHEMAS.map((a) => a.name);
}

export function getAlgorithmSchema(name: string): AlgorithmSchema | undefined {
  return ALGORITHM_SCHEMAS.find((a) => a.name === name);
}

export function listFilterTypes(): string[] {
  return FILTER_SCHEMAS.map((f) => f.type);
}

export function getFilterSchema(type: string): FilterSchema | undefined {
  return FILTER_SCHEMAS.find((f) => f.type === type);
}

function zeroFor(type: ParamType): unknown {
  switch (type) {
    case 'int':
    case 'double':
      return 0;
    case 'bool':
      return false;
    case 'string':
      return '';
    case 'int[]':
    case 'double[]':
    case 'string[]':
      return [];
  }
}

/**
 * Seed a runnable `parameters` map for an algorithm: explicit Lib defaults win,
 * required params without a default get a type-appropriate zero, and optional
 * params without a default are omitted (the user opts in via the form).
 */
export function defaultParameters(name: string): Record<string, unknown> {
  const schema = getAlgorithmSchema(name);
  if (!schema) return {};
  const out: Record<string, unknown> = {};
  for (const p of schema.params) {
    if (p.default !== undefined) out[p.name] = p.default;
    else if (p.required) out[p.name] = zeroFor(p.type);
  }
  return out;
}

/**
 * Params that are fixed by the chosen algorithm (not user-editable — hidden from
 * the form in catalogOverlay) and must always come from the schema default, never
 * be inherited. `by_cutoffs` is `true` for the by-cutoff tonnage variant and
 * `false` for the by-categories one; inheriting it would let a by-categories run
 * carry `true` from a previous by-cutoff algorithm (and vice versa) and be sent
 * with the wrong grouping.
 */
const ALGORITHM_FIXED_PARAMS = new Set(['by_cutoffs']);

/**
 * Seed a new algorithm's `parameters` from a reference algorithm's values so
 * adding another algorithm doesn't force re-typing every field. Only keys the
 * new algorithm's schema declares are carried over (a cross-type reference may
 * hold params the Lib would reject here); anything the reference lacks falls
 * back to the schema default. When the reference is the same algorithm type,
 * this reproduces its schema-declared parameters (any non-schema keys are dropped
 * and missing schema keys fall back to defaults). Algorithm-fixed params (see
 * ALGORITHM_FIXED_PARAMS) are never inherited — they keep the schema default.
 */
export function inheritedParameters(
  name: string,
  reference: Record<string, unknown> | undefined
): Record<string, unknown> {
  const base = defaultParameters(name);
  const schema = getAlgorithmSchema(name);
  if (!reference || !schema) return base;
  const out = { ...base };
  for (const p of schema.params) {
    if (ALGORITHM_FIXED_PARAMS.has(p.name)) continue; // keep the schema default
    // Deep-clone so array/object params aren't shared with the reference algorithm.
    if (reference[p.name] !== undefined) out[p.name] = structuredClone(reference[p.name]);
  }
  return out;
}
